/**
 * ItemCardGrid — card-layout view for the item list.
 *
 * When grouped: renders one card per group (e.g., one card per Collection,
 * Category, Status, etc.) with the matching items listed inside.
 *
 * When not grouped: renders individual item cards in a masonry-style grid.
 *
 * Shares all filter/sort/search state with the table view — toggling between
 * layouts preserves the current query.
 */

import { ChevronDown, ExternalLink, GitBranch, Layers } from 'lucide-react';
import type { ItemRow, GroupedRows } from '../hooks/useItemListData';
import type { SortSpec } from '../hooks/useItemListState';
import { useAltitudeText } from '../../../../hooks/prioritization/useAltitudeText';
import { ObjectRef } from '@/components/ds/ObjectRef';
import { OBJECT_KINDS } from '@/components/ds/objectKinds';
import { CATEGORY_BADGE, CATEGORY_DOT, CATEGORY_BADGE_FALLBACK, CATEGORY_DOT_FALLBACK } from '../../../../lib/itemCategoryColors';

// ============================================================================
// Group-card accent colors (cycles through palette by group index)
// ============================================================================

const GROUP_ACCENTS = [
  { header: 'bg-[var(--slate)]',  badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',  border: 'border-[var(--slate)]' },
  { header: 'bg-[var(--slate)]',  badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',  border: 'border-[var(--slate)]' },
  { header: 'bg-[var(--slate)]',     badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',         border: 'border-[var(--slate)]' },
  { header: 'bg-[var(--green)]', badge: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]', border: 'border-[var(--green)]' },
  { header: 'bg-[var(--mustard)]',   badge: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',     border: 'border-[var(--mustard)]' },
  { header: 'bg-[var(--coral)]',    badge: 'bg-[var(--coral-dim)] text-[var(--coral)]',       border: 'border-[var(--coral)]' },
  { header: 'bg-[var(--green)]',    badge: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',       border: 'border-[var(--green)]' },
  { header: 'bg-[var(--surface)]',   badge: 'bg-[var(--hover-tint)] text-[var(--text-sec)]',     border: 'border-[var(--rule)]' },
];

function accentFor(idx: number) {
  return GROUP_ACCENTS[idx % GROUP_ACCENTS.length];
}

// ============================================================================
// Props
// ============================================================================

interface ItemCardGridProps {
  rows: ItemRow[];
  grouped: GroupedRows[] | null;
  sort: SortSpec[];
  expandedGroups: Set<string>;
  onToggleGroup: (key: string) => void;
  searchQuery: string;
  onRowClick: (item: ItemRow) => void;
  selectedItemId?: string | null;
  queueNameMap?: Record<string, string>;
}

// ============================================================================
// Component
// ============================================================================

export function ItemCardGrid({
  rows,
  grouped,
  expandedGroups,
  onToggleGroup,
  searchQuery,
  onRowClick,
  selectedItemId,
  queueNameMap,
}: ItemCardGridProps) {

  if (grouped && grouped.length > 0) {
    return (
      <div className="flex-1 overflow-auto p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 items-start">
          {grouped.map((group, idx) => (
            <GroupCard
              key={group.key}
              group={group}
              accent={accentFor(idx)}
              expanded={expandedGroups.has(group.key)}
              onToggle={() => onToggleGroup(group.key)}
              onRowClick={onRowClick}
              selectedItemId={selectedItemId}
              searchQuery={searchQuery}
              queueNameMap={queueNameMap}
            />
          ))}
        </div>
      </div>
    );
  }

  // Ungrouped — individual item cards
  if (rows.length === 0) return null;

  return (
    <div className="flex-1 overflow-auto p-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 items-start">
        {rows.map((item) => (
          <ItemCard
            key={item.id}
            item={item}
            onClick={onRowClick}
            isSelected={selectedItemId === item.id}
            searchQuery={searchQuery}
            queueNameMap={queueNameMap}
          />
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Group card (one per group when grouped view is active)
// ============================================================================

interface GroupCardProps {
  group: GroupedRows;
  accent: typeof GROUP_ACCENTS[0];
  expanded: boolean;
  onToggle: () => void;
  onRowClick: (item: ItemRow) => void;
  selectedItemId?: string | null;
  searchQuery: string;
  queueNameMap?: Record<string, string>;
}

const MAX_PREVIEW = 5;

function GroupCard({ group, accent, expanded, onToggle, onRowClick, selectedItemId, searchQuery, queueNameMap }: GroupCardProps) {
  const visibleItems = expanded ? group.rows : group.rows.slice(0, MAX_PREVIEW);
  const hiddenCount = expanded ? 0 : group.rows.length - MAX_PREVIEW;

  return (
    <div className={` border ${accent.border} dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] shadow-sm flex flex-col overflow-hidden`}>
      {/* Header */}
      <div className={`${accent.header} px-4 py-3 flex items-center gap-2`}>
        <span className="flex-1 text-sm font-semibold text-white truncate" title={group.label}>
          {group.kind && <span className="mr-1.5 font-mono text-[11px] uppercase tracking-[0.08em] text-white/80">{OBJECT_KINDS[group.kind].word} ·</span>}
          {group.label}
        </span>
        <span className="flex-shrink-0 text-xs font-bold text-white/80 bg-[var(--bg-raised)]/20 rounded-full px-2 py-0.5 tabular-nums">
          {group.count}
        </span>
      </div>

      {/* Item list */}
      <div className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
        {visibleItems.map((item) => (
          <GroupCardRow
            key={item.id}
            item={item}
            isSelected={selectedItemId === item.id}
            onClick={onRowClick}
            searchQuery={searchQuery}
            queueNameMap={queueNameMap}
          />
        ))}
      </div>

      {/* Footer — show more / collapse */}
      {(hiddenCount > 0 || (expanded && group.rows.length > MAX_PREVIEW)) && (
        <button
          onClick={onToggle}
          className="flex items-center justify-center gap-1.5 px-4 py-2.5 text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-dim)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors border-t border-[var(--rule)] dark:border-[var(--rule)]"
        >
          {expanded ? (
            <>
              <ChevronDown className="h-3.5 w-3.5 rotate-180" />
              Show less
            </>
          ) : (
            <>
              <ChevronDown className="h-3.5 w-3.5" />
              {hiddenCount} more item{hiddenCount !== 1 ? 's' : ''}
            </>
          )}
        </button>
      )}

      {group.rows.length === 0 && (
        <div className="px-4 py-6 text-center text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)]">No items</div>
      )}
    </div>
  );
}

// ============================================================================
// Row inside a group card
// ============================================================================

function GroupCardRow({ item, isSelected, onClick, searchQuery, queueNameMap }: {
  item: ItemRow;
  isSelected: boolean;
  onClick: (item: ItemRow) => void;
  searchQuery: string;
  queueNameMap?: Record<string, string>;
}) {
  const altitudeText = useAltitudeText();
  const dot = item.itemCategory ? CATEGORY_DOT[item.itemCategory] ?? CATEGORY_DOT_FALLBACK : null;

  const title = searchQuery
    ? highlightText(item.title, searchQuery)
    : item.title;

  return (
    <button
      onClick={() => onClick(item)}
      className={`w-full text-left px-4 py-2.5 transition-colors ${
        isSelected
          ? 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30'
          : 'hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]'
      }`}
    >
      <div className="flex items-start gap-2 min-w-0">
        {dot && <span className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${dot}`} />}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate leading-snug">
            {title}
          </p>
          <div className="flex items-center gap-2 mt-0.5">
            {item.altitudeLevel && (
              <ObjectRef kind="altitude" hideWord name={altitudeText.format(item.altitudeLevel, item.itemCategory, { short: true }) ?? String(item.altitudeLevel)} className="text-[10px]" />
            )}
            {item.queueId && queueNameMap?.[item.queueId] && (
              <span className="text-[10px] text-[var(--text-ter)] truncate">{queueNameMap[item.queueId]}</span>
            )}
          </div>
        </div>
        <ExternalLink className="h-3 w-3 text-[var(--text-ter)] flex-shrink-0 mt-0.5 opacity-0 group-hover:opacity-100" />
      </div>
    </button>
  );
}

// ============================================================================
// Individual item card (ungrouped mode)
// ============================================================================

function ItemCard({ item, onClick, isSelected, searchQuery, queueNameMap: _queueNameMap }: {
  item: ItemRow;
  onClick: (item: ItemRow) => void;
  isSelected: boolean;
  searchQuery: string;
  queueNameMap?: Record<string, string>;
}) {
  const altitudeText = useAltitudeText();
  const badge = item.itemCategory ? CATEGORY_BADGE[item.itemCategory] ?? CATEGORY_BADGE_FALLBACK : null;
  const dot   = item.itemCategory ? CATEGORY_DOT[item.itemCategory]   ?? CATEGORY_DOT_FALLBACK   : null;

  const title = searchQuery ? highlightText(item.title, searchQuery) : item.title;

  const statusColor =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (item as any).exit_status === 'archived' ? 'bg-[var(--hover-tint)] text-[var(--text-ter)]' :
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (item as any).exit_status === 'deferred'  ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]' :
    'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]';

  const statusLabel =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (item as any).exit_status === 'archived' ? 'Archived' :
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (item as any).exit_status === 'deferred'  ? 'Deferred' :
    'Active';

  return (
    <button
      onClick={() => onClick(item)}
      className={`w-full text-left  border transition-all duration-150 shadow-sm hover:shadow-md overflow-hidden ${
        isSelected
          ? 'border-[var(--slate)] ring-2 ring-indigo-200 dark:ring-indigo-700 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30'
          : 'border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] hover:border-[var(--rule)] dark:hover:border-[var(--rule-hover)]'
      }`}
    >
      {/* Category accent strip */}
      {dot && (
        <div className={`h-1 w-full ${dot.replace('bg-', 'bg-').replace('-400', '-400')}`} />
      )}

      <div className="p-4">
        {/* Title — a settled cluster shows the cluster's name, not a member's */}
        <p className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] leading-snug line-clamp-2 mb-2">
          {title}
        </p>
        {item.clusterEntry && (
          <p className="flex items-center gap-1 mb-2 font-mono text-[11px] uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-dim)]">
            <Layers className="h-3 w-3 text-[var(--text-ter)]" />
            Cluster · {item.clusterEntry.memberIds.length} items
          </p>
        )}

        {/* Description */}
        {item.description && !item.clusterEntry && (
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)] line-clamp-2 mb-3 leading-relaxed">
            {item.description}
          </p>
        )}

        {/* Badges row */}
        <div className="flex flex-wrap gap-1.5 items-center">
          {badge && item.itemCategory && (
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5  text-[10px] font-medium ${badge.bg} ${badge.text}`}>
              {dot && <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />}
              {item.itemCategory.charAt(0).toUpperCase() + item.itemCategory.slice(1)}
            </span>
          )}
          <span className={`inline-flex items-center px-1.5 py-0.5  text-[10px] font-medium ${statusColor}`}>
            {statusLabel}
          </span>
          {item.altitudeLevel && (
            <ObjectRef kind="altitude" hideWord name={altitudeText.format(item.altitudeLevel, item.itemCategory, { short: true }) ?? String(item.altitudeLevel)} className="text-[10px]" />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[var(--rule)] dark:border-[var(--rule)]">
          {item.collectionNames && item.collectionNames.length > 0 && (
            <span className="flex-1 truncate text-[10px]">
              <ObjectRef kind="collection" hideWord name={`${item.collectionNames.slice(0, 2).join(', ')}${item.collectionNames.length > 2 ? ` +${item.collectionNames.length - 2}` : ''}`} />
            </span>
          )}
          {item.hasChildren && (
            <GitBranch className="h-3 w-3 text-[var(--text-ter)] flex-shrink-0" />
          )}
          {item.tags && item.tags.length > 0 && (
            <span className="text-[10px] text-[var(--text-ter)] flex-shrink-0">
              {item.tags.length} tag{item.tags.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function highlightText(text: string, query: string): React.ReactNode {
  if (!query) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-[var(--mustard-dim)] text-[var(--mustard)] rounded px-0.5">{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  );
}
